import { useState, useCallback, useEffect } from 'react';
import { api } from '../api/client';
import { useVisiblePolling } from './useVisiblePolling';
import { getSseBus } from '../lib/sseBus';
import type { PoolInfo, PoolInstance } from '../api/types';

/**
 * Live pools list — SSE-first (snapshot + pool.member.acquired/released
 * deltas), with polling as a 30 s fallback.
 *
 * The backend doesn't emit a `pool.size` event; member-count changes
 * flow through session.created/session.killed (Cloud's IACP cc91e50e).
 * For acquired/released we patch instance state in place so pool dots
 * reflect the change instantly without a full refetch.
 */
export function usePools(enabled = true, fallbackIntervalMs = 30_000) {
  const [pools, setPools] = useState<PoolInfo[]>([]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await api.pools.all();
      setPools(data.pools);
    } catch { /* silent */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const bus = getSseBus();
    const unsubs: Array<() => void> = [];

    unsubs.push(bus.subscribe('snapshot', (ev) => {
      // Only adopt a non-empty snapshot — a reconnect snapshot that
      // arrives before federation-disco merged remote pools would
      // otherwise blank the pool section (same flicker class as the
      // session list, Alex 2026-06-17).
      if (ev.pools && ev.pools.length > 0) setPools(ev.pools);
    }));

    unsubs.push(bus.subscribe('pool.member.acquired', (ev) => {
      setPools((prev) => patchInstance(prev, ev.pool, ev.name, (inst) => ({
        ...inst,
        acquired_by: ev.acquired_by ?? inst.acquired_by ?? '',
      })));
    }));

    unsubs.push(bus.subscribe('pool.member.released', (ev) => {
      setPools((prev) => patchInstance(prev, ev.pool, ev.name, (inst) => ({
        ...inst,
        acquired_by: '',
      })));
    }));

    return () => { for (const u of unsubs) u(); };
  }, [enabled]);

  useVisiblePolling(refresh, fallbackIntervalMs);

  return { pools, refresh };
}

function patchInstance(
  pools: PoolInfo[],
  poolName: string,
  instanceName: string,
  patcher: (inst: PoolInstance) => PoolInstance,
): PoolInfo[] {
  let touched = false;
  const next = pools.map((p) => {
    if (p.name !== poolName || !p.instances) return p;
    let instancesTouched = false;
    const instances = p.instances.map((inst) => {
      if (inst.name !== instanceName) return inst;
      instancesTouched = true;
      return patcher(inst);
    });
    if (!instancesTouched) return p;
    touched = true;
    return { ...p, instances };
  });
  return touched ? next : pools;
}
